import React, { createContext, useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConnectionStatus from '../components/ConnectionStatus/ConnectionStatus';
import toastr from 'toastr';

const AuthContext = createContext();
const MemoizedAppContent = memo(({ children }) => children);

const HEARTBEAT_TIMEOUT = 45000;

const workerCode = `
  let intervalId;
  self.onmessage = function(e) {
    if (e.data === 'start') {
      intervalId = setInterval(() => postMessage('tick'), 5000);
    } else if (e.data === 'stop') {
      clearInterval(intervalId);
    }
  };
`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const listenersRef = useRef(new Set());
  const failedAttemptsRef = useRef(0);
  const lastWsMessageAtRef = useRef(Date.now());
  const expectTrafficRef = useRef(false);
  const reconnectLockRef = useRef(0);
  const timerWorkerRef = useRef(null);

  // Выделенный WS для снапшотов (webapp-worker, порт 3003, путь /ws-snap)
  const snapWsRef = useRef(null);
  const snapReconnectTimerRef = useRef(null);
  const snapReconnectDelayRef = useRef(1000);
  const snapManualCloseRef = useRef(false);

  // WS throttle: копим некритичные сообщения 200мс и применяем одним батчем,
  // чтобы React не делал reconciliation на каждое из 500+ сообщений/сек в пиках.
  // Срочные события (forceLogout) обходят буфер.
  const msgBufferRef = useRef([]);
  const flushTimerRef = useRef(null);
  const WS_FLUSH_INTERVAL_MS = 200;
  const WS_FLUSH_FORCE_SIZE = 300;

  // Отдельный coalescer для devicesStatusSnapshot: у нас 41 воркер × ~1 snapshot/10с
  // = ~8 сообщений/сек на админа, и один и тот же uuid может приходить в нескольких
  // снапшотах (устройства мигрируют между воркерами). Копим items в Map<uuid→entry>
  // 200мс, потом выплёвываем ОДИН consolidated snapshot. Дубли автоматически
  // дедуплицируются Map.set().
  const snapshotCoalesceMapRef = useRef(null);
  const snapshotCoalesceTimerRef = useRef(null);
  const SNAPSHOT_COALESCE_MS = 1000;

  const navigate = useNavigate();

  const logout = useCallback(() => {
    clearTimeout(reconnectTimerRef.current);
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
    delete api.defaults.headers.common['Authorization'];
    if (wsRef.current && wsRef.current.manualClose) {
      wsRef.current.manualClose();
    }
    wsRef.current = null;
    failedAttemptsRef.current = 0;
    setConnectionStatus('disconnected');
    // Сбрасываем WS-буфер — старые сообщения неактуальны после logout.
    msgBufferRef.current = [];
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
    // Чистим snapshot coalescer
    if (snapshotCoalesceMapRef.current) { snapshotCoalesceMapRef.current.clear(); snapshotCoalesceMapRef.current = null; }
    if (snapshotCoalesceTimerRef.current) { clearTimeout(snapshotCoalesceTimerRef.current); snapshotCoalesceTimerRef.current = null; }
    // Закрываем snap WS
    snapManualCloseRef.current = true;
    clearTimeout(snapReconnectTimerRef.current);
    if (snapWsRef.current) { try { snapWsRef.current.close(1000, 'logout'); } catch (_) {} snapWsRef.current = null; }
    
    if (timerWorkerRef.current) {
      timerWorkerRef.current.postMessage('stop');
    }
    
    navigate('/login');
  }, [navigate]);

  const flushMessages = useCallback(() => {
    const buf = msgBufferRef.current;
    msgBufferRef.current = [];
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (!buf.length) return;
    // Все накопленные сообщения обрабатываются в одном синхронном блоке —
    // React (18+) автоматически батчит setState внутри этого выполнения.
    for (const message of buf) {
      for (const fn of listenersRef.current) {
        try { fn(message); } catch {}
      }
    }
  }, []);

  // Flush coalesced snapshot: собираем все items из Map в один массив,
  // очищаем Map, шлём ОДИН consolidated snapshot всем listener'ам.
  const flushSnapshotCoalesced = useCallback(() => {
    const map = snapshotCoalesceMapRef.current;
    snapshotCoalesceMapRef.current = null; // явная очистка ref
    if (snapshotCoalesceTimerRef.current) {
      clearTimeout(snapshotCoalesceTimerRef.current);
      snapshotCoalesceTimerRef.current = null;
    }
    if (!map || map.size === 0) return;

    const items = [];
    for (const [uuid, entry] of map.entries()) {
      items.push({ uuid, ...entry });
    }
    map.clear(); // явная очистка самого Map перед тем как отпустить

    const consolidated = { action: 'devicesStatusSnapshot', items };
    for (const fn of listenersRef.current) {
      try { fn(consolidated); } catch {}
    }
  }, []);

  const createWebSocket = useCallback((currentToken) => {
    if (!currentToken) return;
    if (wsRef.current) {
        try {
            if (wsRef.current.manualClose) {
                wsRef.current.manualClose();
            } else {
                wsRef.current.close();
            }
        } catch(e) {}
    }

    setConnectionStatus('reconnecting');
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // В dev (.env.development) можно задать REACT_APP_WS_BASE, чтобы идти напрямую
    // на backend, минуя CRA dev-server (его HMR перехватывает upgrade на /ws).
    const wsBase = process.env.REACT_APP_WS_BASE || `${protocol}://${window.location.host}`;
    const wsUrl = `${wsBase}/ws?token=${currentToken}&type=webapp`;

    const socket = new WebSocket(wsUrl);
    let manualClose = false;

    socket.onopen = () => {
      failedAttemptsRef.current = 0;
      setConnectionStatus('connected');
      reconnectDelayRef.current = 1000;
      lastWsMessageAtRef.current = Date.now();
      expectTrafficRef.current = false; 
    };

    socket.onmessage = (event) => {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      lastWsMessageAtRef.current = Date.now();
      expectTrafficRef.current = true;

      if (message.type === 'PING') {
        return;
      }

      if (message.action === 'forceLogout') {
        if (typeof toastr !== 'undefined') {
          toastr.warning(
            ' ',
            message.reason || 'Сессия недействительна. Перезайдите.'
          );
        }
        logout();
        return;
      }

      // devicesStatusSnapshot: 41 воркер × 1 snapshot/10с = ~8/сек, один и тот же uuid
      // может приходить в нескольких snapshot'ах. Склеиваем в Map<uuid→entry> 200мс
      // и выплёвываем один consolidated snapshot — дедупликация через Map.set().
      if (message.action === 'devicesStatusSnapshot') {
        const items = Array.isArray(message.items) ? message.items : [];
        if (items.length === 0) return;
        if (!snapshotCoalesceMapRef.current) snapshotCoalesceMapRef.current = new Map();
        const map = snapshotCoalesceMapRef.current;
        for (const item of items) {
          if (item && item.uuid) {
            const existing = map.get(item.uuid);
            if (!existing || !existing.serverNow || item.serverNow > existing.serverNow) {
              map.set(item.uuid, {
                status: item.status,
                lastSeen: item.lastSeen,
                serverNow: item.serverNow,
              });
            }
          }
        }
        if (!snapshotCoalesceTimerRef.current) {
          snapshotCoalesceTimerRef.current = setTimeout(flushSnapshotCoalesced, SNAPSHOT_COALESCE_MS);
        }
        return;
      }

      // NE-UDALYAT: deviceStatusUpdate puschaem cherez 200ms bufer kak vse ostalnie
      // if (message.action === 'deviceStatusUpdate') {
      // if (msgBufferRef.current.length > 0) flushMessages();
      // for (const fn of listenersRef.current) {
      // try { fn(message); } catch {}
      // }
      // return;
      // }

      // Остальное — буферизируем и flush раз в 200мс ЛИБО когда накопилось >300 сообщений.
      // Так React reconciliation идёт 5 раз/сек вместо 250+ раз/сек.
      msgBufferRef.current.push(message);
      if (msgBufferRef.current.length >= WS_FLUSH_FORCE_SIZE) {
        flushMessages();
      } else if (!flushTimerRef.current) {
        flushTimerRef.current = setTimeout(flushMessages, WS_FLUSH_INTERVAL_MS);
      }
    };

    socket.onclose = (event) => {
      if (!manualClose) {
         setConnectionStatus('reconnecting');
         failedAttemptsRef.current += 1;
         if (failedAttemptsRef.current >= 15) {
           setConnectionStatus('disconnected');
           logout();
           return;
         }
         clearTimeout(reconnectTimerRef.current);
         reconnectTimerRef.current = setTimeout(() => {
           createWebSocket(currentToken);
         }, reconnectDelayRef.current);
         reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30000);
      }
    };

    socket.onerror = () => {
      if (!manualClose) socket.close();
    };

    wsRef.current = socket;
    wsRef.current.manualClose = () => {
      manualClose = true;
      clearTimeout(reconnectTimerRef.current);
      socket.close(1000, 'Manual close');
    };
  }, [logout]);

  // ─── Snap WebSocket (webapp-worker, /ws-snap) ─────────────────────────────
  // Получает ТОЛЬКО devicesStatusSnapshot, доставляет их напрямую из Redis
  // без IPC-задержки кластера. Реконнект независим от основного WS.
  const createSnapWebSocket = useCallback((currentToken) => {
    if (!currentToken) return;
    snapManualCloseRef.current = false;
    clearTimeout(snapReconnectTimerRef.current);

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const snapBase = process.env.REACT_APP_WS_SNAP_BASE || `${protocol}://${window.location.host}`;
    const snap = new WebSocket(`${snapBase}/ws-snap?token=${currentToken}&type=snap`);

    snap.onopen = () => {
      snapReconnectDelayRef.current = 1000;
    };

    snap.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (msg.action !== 'devicesStatusSnapshot') return;
      // Кормим в тот же coalescer что и основной WS
      const items = Array.isArray(msg.items) ? msg.items : [];
      if (items.length === 0) return;
      if (!snapshotCoalesceMapRef.current) snapshotCoalesceMapRef.current = new Map();
      const map = snapshotCoalesceMapRef.current;
      for (const item of items) {
        if (item && item.uuid) {
          const existing = map.get(item.uuid);
          if (!existing || !existing.serverNow || item.serverNow > existing.serverNow) {
            map.set(item.uuid, { status: item.status, lastSeen: item.lastSeen, serverNow: item.serverNow });
          }
        }
      }
      if (!snapshotCoalesceTimerRef.current) {
        snapshotCoalesceTimerRef.current = setTimeout(flushSnapshotCoalesced, SNAPSHOT_COALESCE_MS);
      }
    };

    snap.onclose = () => {
      snapWsRef.current = null;
      if (!snapManualCloseRef.current) {
        const delay = snapReconnectDelayRef.current;
        snapReconnectDelayRef.current = Math.min(delay * 2, 30000);
        snapReconnectTimerRef.current = setTimeout(() => {
          const t = localStorage.getItem('token');
          if (t) createSnapWebSocket(t);
        }, delay);
      }
    };

    snap.onerror = () => { try { snap.close(); } catch (_) {} };

    snapWsRef.current = snap;
  }, [flushSnapshotCoalesced, SNAPSHOT_COALESCE_MS]);

  useEffect(() => {
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    timerWorkerRef.current = new Worker(workerUrl);

    timerWorkerRef.current.onmessage = (e) => {
      if (e.data === 'tick') {
        const ws = wsRef.current;
        if (!ws) return;

        if (document.visibilityState === 'hidden') return;

        const isOpen = ws.readyState === WebSocket.OPEN;
        if (!isOpen) return;

        if (isOpen) {
            ws.send(JSON.stringify({ action: 'client_ping' }));
        }

        const now = Date.now();
        const msSinceLast = now - lastWsMessageAtRef.current;

        if (msSinceLast > HEARTBEAT_TIMEOUT) {
            console.warn('Сервер не отвечает более 45 секунд. Принудительный реконнект...');
            try {
              ws.close(4000, 'stale-connection-watchdog');
            } catch {}
        }
      }
    };

    timerWorkerRef.current.postMessage('start');

    return () => {
      if (timerWorkerRef.current) {
        timerWorkerRef.current.terminate();
      }
      URL.revokeObjectURL(workerUrl);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const msSinceLast = now - lastWsMessageAtRef.current;
        
        if (msSinceLast > HEARTBEAT_TIMEOUT) {
            forceWsReconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      try {
        const response = await api.get('/auth/check');
        const { user: serverUser } = response.data;
        setUser(serverUser);
        setToken(storedToken);
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    };
    verifyAuth();
  }, [logout]);

  // Синхронизация между вкладками: если другой таб залогинился или вышел — реагируем немедленно.
  useEffect(() => {
    const handleStorage = async (event) => {
      if (event.key !== 'token') return;
      if (event.newValue && !user) {
        // Другой таб залогинился — подхватываем его сессию без нового логина
        try {
          const response = await api.get('/auth/check');
          const { user: serverUser } = response.data;
          setUser(serverUser);
          setToken(event.newValue);
          navigate('/dashboard');
        } catch {
          localStorage.removeItem('token');
        }
      } else if (!event.newValue && user) {
        // Другой таб вышел — синхронизируем этот таб
        logout();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user, logout, navigate]);

  useEffect(() => {
    if (token && user) {
      createWebSocket(token);
      createSnapWebSocket(token);
    }
    return () => {
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.manualClose();
      }
      // Закрываем snap WS при размонтировании / смене токена
      snapManualCloseRef.current = true;
      clearTimeout(snapReconnectTimerRef.current);
      if (snapWsRef.current) { try { snapWsRef.current.close(1000, 'cleanup'); } catch (_) {} snapWsRef.current = null; }
    };
  }, [token, user, createWebSocket, createSnapWebSocket]);

  const login = useCallback(async (loginName, password) => {
    const response = await api.post('/auth/login', { login: loginName, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    navigate('/dashboard');
  }, [navigate]);

  const registerOnWSMessage = useCallback((callback) => {
    if (callback == null) {
      listenersRef.current.clear();
      return () => {};
    }
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  const forceWsReconnect = useCallback(() => {
    const now = Date.now();

    if (now - reconnectLockRef.current < 5000) {
        return;
    }

    reconnectLockRef.current = now;
    lastWsMessageAtRef.current = now;

    const ws = wsRef.current;

    if (!ws) {
      if (token && user) {
        createWebSocket(token);
      }
      return;
    }

    if (ws.readyState === WebSocket.CONNECTING) {
        return;
    }

    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSING) {
      try {
        ws.manualClose(); 
      } catch {}
      setTimeout(() => {
          if (token && user) {
             createWebSocket(token);
          }
      }, 300);
      return;
    }

    if (token && user) {
      createWebSocket(token);
    }
  }, [createWebSocket, token, user]);

  const ctxValue = useMemo(
    () => ({
      user,
      token,
      loading,
      login,
      logout,
      ws: wsRef,
      registerOnWSMessage,
      connectionStatus,
      forceWsReconnect
    }),
    [user, token, loading, login, logout, registerOnWSMessage, connectionStatus, forceWsReconnect]
  );

  if (loading) {
    return <div>Проверка сессии...</div>;
  }

  return (
    <React.Fragment>
      <ConnectionStatus status={connectionStatus} />
      <AuthContext.Provider value={ctxValue}>
        <MemoizedAppContent>{children}</MemoizedAppContent>
      </AuthContext.Provider>
    </React.Fragment>
  );
};

export default AuthContext;